/**
 * Petty Cash Floats (GAP-012) — the float register and each float's movement statement.
 *
 * A float is a Petty Cash GL account someone holds cash against. Top-ups bring cash in, payments and
 * returns take it out; every movement posts its own balanced journal, so the Petty Cash GL always
 * equals the floats on it. The never-negative rule (Q8) is enforced server-side — a payment or return
 * larger than the balance is refused with a plain message shown here.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus, ArrowDownCircle, ArrowUpCircle, Undo2, Lock, X, Wallet, AlertTriangle,
  BookOpen, LayoutGrid } from 'lucide-react';

import { pettyCashApi }        from '@/api/pettyCash.api';
import { AccountLookupField }  from '@/components/erp/AccountLookupField';
import { LocationLookup }      from '@/components/lookups/EntityLookups';
import { usePermission }       from '@/hooks/usePermission';
import { Permissions }         from '@/constants/permissions';
import { AppRoutes }           from '@/constants/routes';
import { Spinner }             from '@/components/ui/Spinner';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { cn }                  from '@/utils/cn';
import type {
  PettyCashFloatListDto, PettyCashFloatDetailDto,
} from '@/types/pettyCash.types';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const MOVEMENT_META: Record<string, { label: string; cls: string }> = {
  TOPUP:   { label: 'Top-up',  cls: 'text-emerald-700 dark:text-emerald-400' },
  PAYMENT: { label: 'Payment', cls: 'text-red-600 dark:text-red-400' },
  RETURN:  { label: 'Return',  cls: 'text-amber-600 dark:text-amber-400' },
};

export const PettyCashFloatsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canView   = hasPermission(Permissions.PTY_FLOAT_VIEW);
  const canCreate = hasPermission(Permissions.PTY_FLOAT_CREATE);
  const canTopUp  = hasPermission(Permissions.PTY_FLOAT_TOPUP);
  const canPay    = hasPermission(Permissions.PTY_FLOAT_PAY);
  const canReturn = hasPermission(Permissions.PTY_FLOAT_RETURN);
  const canClose  = hasPermission(Permissions.PTY_FLOAT_CLOSE);

  const [floats,  setFloats]  = useState<PettyCashFloatListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [openId,  setOpenId]  = useState<string | null>(null);
  const [detail,  setDetail]  = useState<PettyCashFloatDetailDto | null>(null);
  const [creating, setCreating] = useState(false);

  const [closing, setClosing] = useState<PettyCashFloatListDto | null>(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await pettyCashApi.list();
      setFloats(res.data?.data ?? []);
    } catch (e) { console.error('Petty cash load failed', e); }
    finally { setLoading(false); }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  const openStatement = async (id: string) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null);
    try {
      const res = await pettyCashApi.get(id);
      setDetail(res.data?.data ?? null);
    } catch (e) { console.error('Statement load failed', e); }
  };

  const afterChange = async () => {
    await load();
    if (openId) { const res = await pettyCashApi.get(openId); setDetail(res.data?.data ?? null); }
  };

  const closeFloat = (f: PettyCashFloatListDto) => {
    setCloseError(null);
    if (f.currentBalance !== 0) {
      setCloseError(`Float ${f.floatCode} still holds ${fmt(f.currentBalance)}. Return it to a bank account before closing.`);
      return;
    }
    setClosing(f);
  };

  const executeClose = async () => {
    if (!closing) return;
    setCloseLoading(true);
    try {
      await pettyCashApi.close(closing.id);
      setClosing(null);
      await afterChange();
    } catch (e: any) {
      setCloseError(e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.message ?? 'Close failed.');
    } finally { setCloseLoading(false); }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p className="text-sm">You do not have permission to view petty cash floats.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Wallet size={20} /> Petty Cash Floats
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {floats.length} float{floats.length !== 1 ? 's' : ''} · click one for its statement
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(AppRoutes.PETTY_CASH_SUMMARY)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <LayoutGrid size={14} /> Summary
            </button>
            <button onClick={() => navigate(AppRoutes.PETTY_CASH_REGISTER)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <BookOpen size={14} /> Register
            </button>
            <button onClick={() => navigate(AppRoutes.PETTY_CASH_VOUCHERS)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Wallet size={14} /> Vouchers
            </button>
            {canCreate && (
              <button onClick={() => setCreating(true)}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus size={14} /> New Float
              </button>
            )}
            <button onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 transition-colors disabled:opacity-40" title="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {closeError && (
        <div className="flex items-center gap-2 px-6 py-2.5 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-b border-red-200 dark:border-red-800">
          <AlertTriangle size={15} className="shrink-0" />
          {closeError}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : floats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Wallet size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No petty cash floats yet.</p>
          </div>
        ) : (
          floats.map(f => {
            const isOpen = openId === f.id;
            const active = f.status === 'ACTIVE';
            return (
              <div key={f.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
                {/* Row */}
                <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30"
                  onClick={() => openStatement(f.id)}>
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white text-sm flex items-center gap-2">
                        {f.floatName}
                        <span className="text-[11px] font-mono text-slate-400">{f.floatCode}</span>
                        {!active && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 ring-1 ring-slate-200">Closed</span>}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {f.glAccountCode} · {f.glAccountName}{f.custodian ? ` · ${f.custodian}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">Balance</div>
                      <div className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{fmt(f.currentBalance)} {f.currencyCode}</div>
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">Limit</div>
                      <div className="font-mono text-xs text-slate-500">{fmt(f.floatLimit)}</div>
                    </div>
                  </div>
                </div>

                {/* Statement (expanded) */}
                {isOpen && (
                  <div className="border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 px-4 py-3">
                    {active && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {canTopUp && <ActionBtn icon={<ArrowDownCircle size={13} />} label="Top up" onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHER_NEW}?float=${f.id}&type=TOPUP`)} />}
                        {canPay   && <ActionBtn icon={<ArrowUpCircle size={13} />}   label="Pay"    onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHER_NEW}?float=${f.id}&type=PAYMENT`)} />}
                        {canReturn && <ActionBtn icon={<Undo2 size={13} />}          label="Return to bank" onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHER_NEW}?float=${f.id}&type=RETURN`)} />}
                        {canClose && <ActionBtn icon={<Lock size={13} />}            label="Close" onClick={() => closeFloat(f)} />}
                      </div>
                    )}
                    {!detail ? (
                      <div className="flex items-center justify-center py-6"><Spinner size="sm" /></div>
                    ) : detail.movements.length === 0 ? (
                      <p className="text-xs text-slate-400 py-3">No movements yet.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] text-slate-400 uppercase tracking-wider">
                            <th className="text-left font-semibold py-1.5">Date</th>
                            <th className="text-left font-semibold py-1.5">Type</th>
                            <th className="text-left font-semibold py-1.5">Description</th>
                            <th className="text-right font-semibold py-1.5">Amount</th>
                            <th className="text-right font-semibold py-1.5">Balance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                          {detail.movements.map(m => {
                            const meta = MOVEMENT_META[m.movementType] ?? { label: m.movementType, cls: 'text-slate-600' };
                            const outflow = m.movementType !== 'TOPUP';
                            return (
                              <tr key={m.id}>
                                <td className="py-1.5 text-xs text-slate-500 font-mono">{m.movementDate}</td>
                                <td className={cn('py-1.5 text-xs font-medium', meta.cls)}>{meta.label}</td>
                                <td className="py-1.5 text-xs text-slate-600 dark:text-slate-300">{m.description || m.reference || '—'}</td>
                                <td className={cn('py-1.5 text-right font-mono text-xs', meta.cls)}>{outflow ? '-' : '+'}{fmt(m.amount)}</td>
                                <td className="py-1.5 text-right font-mono text-xs text-slate-900 dark:text-white">{fmt(m.balanceAfter)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {creating && <CreateFloatModal onClose={() => setCreating(false)} onCreated={async () => { setCreating(false); await load(); }} />}

      <ConfirmDialog
        isOpen={closing !== null}
        variant="warning"
        title="Close Float"
        message={<span>Close float <strong>{closing?.floatCode}</strong>?</span>}
        confirmLabel="Yes, Close"
        cancelLabel="Cancel"
        loading={closeLoading}
        onConfirm={executeClose}
        onCancel={() => setClosing(null)}
      />
    </div>
  );
};

// ── Small action button ──────────────────────────────────────────────────────
const ActionBtn = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors">
    {icon} {label}
  </button>
);

// ── Create float modal ───────────────────────────────────────────────────────
const CreateFloatModal = ({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [glAccountId, setGl] = useState<string | null>(null);
  const [glCode, setGlCode] = useState<string | null>(null);
  const [glName, setGlName] = useState<string | null>(null);
  const [limit, setLimit] = useState('');
  const [custodian, setCustodian] = useState('');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    if (!code.trim() || !name.trim()) { setError('Give the float a code and a name.'); return; }
    if (!glAccountId) { setError('Choose the Petty Cash GL account this float posts against.'); return; }
    setSaving(true);
    try {
      await pettyCashApi.create({
        glAccountId, floatCode: code.trim(), floatName: name.trim(),
        floatLimit: Number(limit) || 0, currencyCode: 'LKR', custodian: custodian.trim() || null,
        locationId: locationId || null,
      });
      onCreated();
    } catch (e: any) {
      setError(e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.message ?? 'Create failed.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New Petty Cash Float" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code"><input value={code} onChange={e => setCode(e.target.value)} className={INPUT} placeholder="PC-001" /></Field>
          <Field label="Name"><input value={name} onChange={e => setName(e.target.value)} className={INPUT} placeholder="Front desk float" /></Field>
        </div>
        <AccountLookupField label="Petty Cash GL account" value={glAccountId} displayCode={glCode} displayName={glName}
          isPostingAccount onChange={(id, c, n) => { setGl(id); setGlCode(c); setGlName(n); }} placeholder="Choose account…" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Float limit"><input value={limit} onChange={e => setLimit(e.target.value)} type="number" className={INPUT} placeholder="0.00" /></Field>
          <Field label="Custodian (optional)"><input value={custodian} onChange={e => setCustodian(e.target.value)} className={INPUT} placeholder="Name" /></Field>
        </div>
        {/* Where the tin physically sits. The float can only be spent from here; left blank it is
            company-wide and reachable from anywhere. The field existed end to end — command,
            entity, list response — and this form was the one place that never asked. */}
        <LocationLookup label="Location (optional — blank means company-wide)"
          value={locationId} onChange={v => setLocationId(v ?? null)} />
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
      <ModalFooter onClose={onClose} onSave={save} saving={saving} saveLabel="Create Float" />
    </Modal>
  );
};

// Top-up / Pay / Return used to be instant-post modals here — no draft, no second pair of eyes,
// nothing to print or reverse. They are now full vouchers: see PettyCashVoucherFormPage, reached
// via the "Top up" / "Pay" / "Return to bank" buttons above.

// ── Tiny modal primitives ────────────────────────────────────────────────────
const INPUT = 'w-full text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</label>
    {children}
  </div>
);

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-900 dark:text-white">{title}</h2>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400"><X size={16} /></button>
      </div>
      {children}
    </div>
  </div>
);

const ModalFooter = ({ onClose, onSave, saving, saveLabel }: { onClose: () => void; onSave: () => void; saving: boolean; saveLabel: string }) => (
  <div className="flex items-center gap-2 mt-5">
    <button onClick={onSave} disabled={saving}
      className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
      {saving ? 'Saving…' : saveLabel}
    </button>
    <button onClick={onClose} className="px-4 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
  </div>
);
